import { registerPlugin, Capacitor } from "@capacitor/core";
import type { BackgroundGeolocationPlugin } from "@capacitor-community/background-geolocation";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { db, crashlytics } from "./firebase";

const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>(
  "BackgroundGeolocation",
);

// Extended type for actual location data received from the plugin
interface LocationData {
  latitude: number;
  longitude: number;
  accuracy?: number;
  speed?: number;
  altitude?: number;
  altitudeAccuracy?: number;
  bearing?: number | null;
  time?: number;
  simulated?: boolean;
}

let watcherId: string | null = null;
let lastHistoryCoords: { lat: number; lng: number } | null = null;
let burstTimer: ReturnType<typeof setTimeout> | null = null;

const isNative = Capacitor.isNativePlatform();
const LOCATION_HISTORY_DISTANCE_METERS = 200;
const LOCATION_HISTORY_RETENTION_DAYS = 30;
const MAX_ACCEPTABLE_ACCURACY_METERS = 100; // Filter out low accuracy locations
const DEFAULT_DISTANCE_FILTER_METERS = 100; // Throttle updates to reduce wakeups/indicators
const DEFAULT_BURST_DURATION_MS = 30 * 1000; // 30s precise burst

const toRadians = (value: number) => (value * Math.PI) / 180;

const getDistanceMeters = (
  from: { lat: number; lng: number },
  to: { lat: number; lng: number },
) => {
  const earthRadiusMeters = 6371000;
  const deltaLat = toRadians(to.lat - from.lat);
  const deltaLng = toRadians(to.lng - from.lng);
  const lat1 = toRadians(from.lat);
  const lat2 = toRadians(to.lat);

  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusMeters * c;
};

type LocationStartOptions = {
  distanceFilter?: number;
  backgroundTitle?: string;
  backgroundMessage?: string;
};

export const startBackgroundLocationUpdates = async (
  userId: string,
  options?: LocationStartOptions,
) => {
  if (!isNative || !db || !userId) return;
  if (watcherId) {
    return;
  }

  try {
    if (!lastHistoryCoords) {
      try {
        const userSnap = await getDoc(doc(db, "users", userId));
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const coords = userData?.lastHistoryCoordinates;
          if (coords?.lat && coords?.lng) {
            lastHistoryCoords = { lat: coords.lat, lng: coords.lng };
          }
        }
      } catch (readError) {
        crashlytics.logError(
          `Failed to load history coords: ${JSON.stringify(readError?.message)}`,
          "backgroundLocationService.ts",
          readError,
        );
      }
    }

    watcherId = await BackgroundGeolocation.addWatcher(
      {
        requestPermissions: true,
        stale: false,
        distanceFilter: options?.distanceFilter ?? DEFAULT_DISTANCE_FILTER_METERS,
        backgroundTitle: options?.backgroundTitle || "Who's in?",
        backgroundMessage: options?.backgroundMessage || "Finding nearby activity matches.",
      },
      async (location, error) => {
        if (error) {
          crashlytics.logError(
            `Background location error: ${JSON.stringify(error)}`,
            "backgroundLocationService.ts",
            error,
          );
          return;
        }

        if (!location) {
          crashlytics.logError(
            "Background location: Received null location data",
            "backgroundLocationService.ts",
          );
          return;
        }

        // Cast to extended type with all properties
        const locationData = location as unknown as LocationData;

        // Extract and validate location data
        const {
          latitude,
          longitude,
          accuracy,
          speed,
          altitude,
          time,
          simulated,
        } = locationData;

        // Validate coordinates
        if (typeof latitude !== "number" || typeof longitude !== "number") {
          crashlytics.logError(
            `Background location: Invalid coordinate types (lat: ${latitude}, lng: ${longitude})`,
            "backgroundLocationService.ts",
          );
          return;
        }
        if (latitude === 0 && longitude === 0) {
          crashlytics.logError(
            `Background location: Ignoring null island coordinates (0,0)`,
            "backgroundLocationService.ts",
          );
          return;
        }
        if (
          latitude < -90 ||
          latitude > 90 ||
          longitude < -180 ||
          longitude > 180
        ) {
          crashlytics.logError(
            `Background location: Out of range coordinates (${latitude}, ${longitude})`,
            "backgroundLocationService.ts",
          );
          return;
        }

        // Filter low accuracy locations
        if (accuracy && accuracy > MAX_ACCEPTABLE_ACCURACY_METERS) {
          crashlytics.logError(
            `Background location: Skipping low accuracy location (${accuracy}m)`,
            "backgroundLocationService.ts",
          );
          return;
        }

        // Flag simulated locations in production
        if (simulated) {
          crashlytics.logError(
            `Background location: Simulated location detected (lat: ${latitude}, lng: ${longitude}, accuracy: ${accuracy}m)`,
            "backgroundLocationService.ts",
          );
        }

        try {
          const currentCoords = {
            lat: latitude,
            lng: longitude,
          };
          const shouldWriteHistory =
            !lastHistoryCoords ||
            getDistanceMeters(lastHistoryCoords, currentCoords) >=
              LOCATION_HISTORY_DISTANCE_METERS;
          const expiresAt = Timestamp.fromDate(
            new Date(
              Date.now() +
                LOCATION_HISTORY_RETENTION_DAYS * 24 * 60 * 60 * 1000,
            ),
          );
          const userUpdates: Record<string, unknown> = {
            lastKnownCoordinates: {
              lat: currentCoords.lat,
              lng: currentCoords.lng,
            },
            lastKnownCoordinatesUpdatedAt: serverTimestamp(),
          };

          if (shouldWriteHistory) {
            await addDoc(collection(db, "users", userId, "locationHistory"), {
              coordinates: currentCoords,
              accuracy: accuracy || null,
              speed: speed || null,
              altitude: altitude || null,
              capturedAt: time ? Timestamp.fromMillis(time) : serverTimestamp(),
              recordedAt: serverTimestamp(),
              simulated: simulated || false,
              expiresAt,
            });
            userUpdates.lastHistoryCoordinates = currentCoords;
            userUpdates.lastHistoryCoordinatesUpdatedAt = serverTimestamp();
            lastHistoryCoords = currentCoords;
          }

          await updateDoc(doc(db, "users", userId), userUpdates);
        } catch (updateError: any) {
          crashlytics.logError(
            `Error updating background coordinates: ${updateError?.message || "Unknown error"}`,
            "backgroundLocationService.ts",
            updateError,
          );
        }
      },
    );
  } catch (error: any) {
    crashlytics.logError(
      `Failed to start background location: ${error?.message || "Unknown error"}`,
      "backgroundLocationService.ts",
      error,
    );
  }
};

export const stopBackgroundLocationUpdates = async () => {
  if (!isNative || !watcherId) {
    return;
  }

  try {
    await BackgroundGeolocation.removeWatcher({ id: watcherId });
  } catch (error: any) {
    crashlytics.logError(
      `Failed to stop background location: ${error?.message || "Unknown error"}`,
      "backgroundLocationService.ts",
      error,
    );
  } finally {
    if (burstTimer) {
      clearTimeout(burstTimer);
      burstTimer = null;
    }
    watcherId = null;
    lastHistoryCoords = null;
  }
};

// Start a short, precise burst and stop automatically (to reduce indicators/energy).
export const startBurstLocationSample = async (
  userId: string,
  options?: LocationStartOptions & { durationMs?: number },
) => {
  if (!isNative || !db || !userId) return;

  const durationMs = options?.durationMs ?? DEFAULT_BURST_DURATION_MS;
  await startBackgroundLocationUpdates(userId, options);

  if (burstTimer) {
    clearTimeout(burstTimer);
  }
  burstTimer = setTimeout(() => {
    stopBackgroundLocationUpdates().catch((err) => {
      crashlytics.logError(
        `Failed to stop burst location: ${err?.message || err}`,
        "backgroundLocationService.ts",
        err,
      );
    });
  }, durationMs);
};
